import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';

// OUTGLOW fix B06 (21/10/2026): esperas (ms) entre as novas tentativas quando /recommendations/products falha
// (429, 5xx ou queda de rede). Antes a seção era escondida na 1ª falha e nunca voltava.
// Revisado 22/10/2026: 2 tentativas (8 s) não cobriam um bloqueio 429 da Shopify; agora são ~2 min no total, e
// mesmo depois de esconder a seção ela tenta de novo quando a rede volta ou o cliente volta para a aba.
const RECOMMENDATIONS_RETRY_DELAYS = [2000, 6000, 15000, 30000, 60000];
const RECOMMENDATIONS_MAX_RETRY_AFTER = 60000;

class ProductRecommendations extends Component {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * OUTGLOW fix B06 (21/10/2026): quantas novas tentativas já foram feitas e o timer da próxima.
   * @type {number}
   */
  #retryCount = 0;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #retryTimer = null;

  /**
   * OUTGLOW fix B06 (22/10/2026): a Shopify respondeu sem nenhuma recomendação (não é falha de rede);
   * nesse caso a seção fica escondida e não tenta de novo ao voltar a rede ou a aba.
   */
  #noResults = false;

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
    // OUTGLOW fix B06 (21/10/2026): quando a conexão volta, tenta de novo (também depois de ter escondido).
    window.addEventListener('online', this.#handleOnline);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
    // OUTGLOW fix B06 (21/10/2026)
    window.removeEventListener('online', this.#handleOnline);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    if (this.#retryTimer) clearTimeout(this.#retryTimer);
    this.#retryTimer = null;
  }

  /**
   * OUTGLOW fix B06 (21/10/2026): a rede voltou. Se as recomendações ainda não carregaram (esperando nova
   * tentativa ou já escondidas por falha), tenta agora, com o ciclo de tentativas zerado.
   */
  #handleOnline = () => {
    if (this.dataset.recommendationsPerformed === 'true' || this.#noResults) return;
    // Nada falhou ainda (a seção nem chegou perto da tela): o IntersectionObserver cuida disso.
    if (!this.#retryTimer && !this.dataset.error) return;
    if (this.#retryTimer) clearTimeout(this.#retryTimer);
    this.#retryTimer = null;
    this.#retryCount = 0;
    this.#loadRecommendations();
  };

  /**
   * OUTGLOW fix B06 (22/10/2026): o cliente voltou para a aba. Se a seção já foi escondida por falha (as
   * tentativas acabaram), tenta de novo com o ciclo zerado. Com uma tentativa agendada, deixa o timer seguir.
   */
  #handleVisibilityChange = () => {
    if (document.visibilityState !== 'visible' || !navigator.onLine) return;
    if (this.dataset.recommendationsPerformed === 'true' || this.#noResults) return;
    if (!this.dataset.error || this.#retryTimer) return;
    this.#retryCount = 0;
    this.#loadRecommendations();
  };

  /**
   * OUTGLOW fix B06 (21/10/2026): falha temporária (429, 5xx, rede). Agenda nova tentativa com espera
   * crescente (respeitando Retry-After); só esconde a seção quando as tentativas acabarem.
   * @param {Error} error
   * @param {number} [retryAfterMs]
   */
  #handleTransientFailure(error, retryAfterMs = 0) {
    if (this.#retryTimer) return;
    const delay = RECOMMENDATIONS_RETRY_DELAYS[this.#retryCount];
    if (delay === undefined) {
      this.#handleError(error);
      return;
    }
    this.#retryCount++;
    const wait = Math.min(Math.max(delay, retryAfterMs), RECOMMENDATIONS_MAX_RETRY_AFTER);
    console.warn(`Product recommendations: ${error.message}; nova tentativa ${this.#retryCount} em ${wait} ms`);
    this.#retryTimer = setTimeout(() => {
      this.#retryTimer = null;
      this.#loadRecommendations();
    }, wait);
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;

    if (!productId || !sectionId) {
      throw new Error('Product ID and a section ID are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            // OUTGLOW fix B06 (21/10/2026): tenta de novo em vez de esconder na 1ª falha.
            this.#handleTransientFailure(new Error(`Server returned ${result.status}`), result.retryAfterMs);
          }
          return;
        }

        if (result.data?.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          // OUTGLOW fix B06 (21/10/2026): se tinha sido escondida por falha e uma tentativa posterior
          // (evento 'online') deu certo, mostra de novo.
          this.#retryCount = 0;
          if (this.dataset.error) {
            delete this.dataset.error;
            this.classList.remove('hidden');
          }
          morphSection(sectionId, result.data, { mode: 'hydration', injectStylesheet: true });
        } else {
          this.#noResults = true;
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        // OUTGLOW fix B06 (21/10/2026): pedido cancelado por um pedido mais novo não é falha; erro de rede
        // (Failed to fetch, corpo interrompido) é temporário e ganha nova tentativa.
        if (e?.name === 'AbortError') return;
        this.#handleTransientFailure(e);
      });
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number, retryAfterMs: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        // OUTGLOW fix B06 (21/10/2026): repassa o Retry-After (em segundos) para a espera da nova tentativa.
        const retryAfterMs = (Number(response.headers.get('Retry-After')) || 0) * 1000;
        return { success: false, status: response.status, retryAfterMs };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
